import { LineItem, Order, OriginItem } from "@redotech/redo-model/order";
import { Channel, SalesChannels } from "./sales-channels";
import { DiscountDistributionMethod } from "./team";

export const INSTANT_REFUND_FEE_PERCENTAGE = 0.05;

export type ReturnTotals = {
  productTotals: ProductTotals[];
  totalStoreCredit: number;
  totalRefund: number;
  totalReturnValue: number; // including taxes and adjustments
  returnValueTax: number; // just the taxes for items being returned
  totalRawReturnValue: number; // not including taxes or adjustments
  totalAdjustmentValue: number; // sum total of all upsells and fees (not including merchant adjustment)
  totalDiscount: number; // not including Redo discounts
  newOrderValue: number; // includes discounts and taxes
  rawNewOrderValue: number; // not including taxes or adjustments
  newOrderDiscount: number;
  newOrderTaxes: number;
  totalTaxes: number; // from the original order
  // does not include taxes. Used for calculating the draft order price
  // (taxes will be added on to the draft order by shopify)
  charge: number;
  displayCharge: number; // takes into account taxes from new order and original order
  recoveryFee: number;
  shippingFee: number;
  shippingFeeDeducted: number;
  instantRefundFee?: number;
  // If the merchant has preDiscountPrice enabled, the difference between the original price and the discount price
  // (can be used for shop now but doesn't roll over as excess store credit)
  nonAccruableCredit: number;
  // Accruable credit that was not used to cover the exchange
  nonAccruableLeftover: number;
};

export type ProductTotals = {
  product: Product;
  discountPrice: number; // includes discounts (if any), does not include tax or price adjustment
  originalPrice: number; // does not include discounts, tax, or price adjustment
  finalPrice: number; // includes discounts, tax, price adjustment, and merchant adjustment (if any)
  priceAdjustment: number;
  merchantAdjustment: number;
  tax: number;
  totalDiscount: number; // not including Redo discounts
};

export type Product = {
  _id?: string;
  line_item_id: string;
  price: string;
  price_adjustment?: string | undefined;
  merchant_adjustment?: string;
  strategy: string;
  status?: string;
  originalVariantOptions?: {
    strategy: string;
    price_adjustment?: string;
  };
  exchangeGroupItem?: Object;
  isUnbundled?: boolean;
} & (
  | {
      new_item?: Object;
    }
  | {
      exchange_for?: Object;
    }
);

type DiscountAllocation = {
  discountedAmount: {
    amount: string;
  };
};

type Refund = {
  refund: {
    id: number;
    refund_line_items: {
      line_item_id: number;
      subtotal: number;
      total_tax: number;
      subtotal_set: {
        presentment_money: {
          currency_code: string;
        };
      };
    }[];
  };
};

type Return = {
  refunds?: Refund[];
  products: Product[];
  provision: string;
  stripe?: any;
  totals: {
    fee: number;
  };
  new_order_taxes?: string;
  discount_allocations?: DiscountAllocation[];
  advancedExchangeItems: { price: string }[];
  shippingFeeToDeduct?: number;
  draftOrderId?: number;
  labelDeductedFromCredit?: boolean;
  originItems?: OriginItem[];
  originalReturnOptions?: {
    advancedExchangeItems?: { price: string }[];
    new_order_taxes?: string;
    discount_allocations: DiscountAllocation[];
  };
  usePreDiscountPrice?: boolean;
};

type Totals = {
  amount: number;
  tax: number;
};

type Team = {
  settings: {
    deductLabelFromCredit?: boolean;
    deductLabelFromRefund?: boolean;
    discountDistributionMethod?: DiscountDistributionMethod;
    exchanges: {
      usePreDiscountPrice?: boolean;
      excessExchangeValue: "store_credit" | "refund";
    };
  };
};

export class ReturnTotalsCalculator {
  private return_: Return;
  private productTotals: ProductTotals[];
  private order: Order;
  private team: Team;
  private isReturnPortal: boolean;
  // Merchants can edit the return after submission. If enabled, we will calculate
  // the totals for the original return
  private useOriginalReturn: boolean;

  constructor(
    return_: Return,
    order: Order,
    team: Team,
    { useOriginalReturn = false }: { useOriginalReturn?: boolean } = {},
  ) {
    this.return_ = return_;
    this.productTotals = [];
    this.order = order;
    this.team = team;
    this.useOriginalReturn = useOriginalReturn || false;
    this.calculateTotalsForProducts();
    // If a product has an _id, it means we are not on the return portal
    // (i.e. the return has already been created)
    this.isReturnPortal = !this.return_.products[0]?._id;
  }

  static getTaxForLineItem(lineItem: LineItem | undefined) {
    let taxValue = 0.0;
    const taxLines = lineItem?.tax_lines || [];
    for (const taxLine of taxLines) {
      taxValue += parseFloat(taxLine.price) / (lineItem?.quantity || 1);
    }

    return Math.floor(taxValue * 100) / 100;
  }

  static getTaxForBundledPart(part: any, bundleLineItem: any, order: any) {
    const discountPrice = ReturnTotalsCalculator.getDiscountPrice(
      bundleLineItem,
      order,
    );
    const percentOfBundle = parseFloat(part.price) / parseFloat(discountPrice);

    return (
      ReturnTotalsCalculator.getTaxForLineItem(bundleLineItem) * percentOfBundle
    );
  }

  /**
   * Get taxes for a specific product in return
   */
  productTax(product: any): number {
    const shopifyLineItem = ReturnTotalsCalculator.findShopifyLineItem(
      product.line_item_id,
      this.order,
    );
    if (product.isUnbundled) {
      return ReturnTotalsCalculator.getTaxForBundledPart(
        product,
        shopifyLineItem,
        this.order,
      );
    }
    const originTax = ReturnTotalsCalculator.getOriginItemTax(
      shopifyLineItem,
      this.order,
    );

    return originTax
      ? parseFloat(originTax)
      : ReturnTotalsCalculator.getTaxForLineItem(shopifyLineItem);
  }

  /**
   * Get taxes for entire return
   */
  returnTax(return_: Return): number {
    let taxValue = 0.0;
    const products = return_.products || [];
    for (const product of products) {
      taxValue += this.productTax(product);
    }
    return parseFloat(taxValue.toFixed(2));
  }

  /**
   * Get the discount for a specific line item. This may not be the same as the discount
   * that was applied to the line item in the original order, depending on the
   * distributionMethod parameter.
   * @param lineItem
   * @param order
   * @param distributionMethod
   * @returns
   */
  private static getDiscountAllocations(
    lineItem: LineItem | undefined,
    order: Order,
    distributionMethod: DiscountDistributionMethod = DiscountDistributionMethod.LINE_ITEM,
  ) {
    if (!lineItem) {
      return 0;
    }

    const discountAllocations = [...lineItem?.discount_allocations];

    for (let i = 0; i < order.shopify.line_items.length; i++) {
      const item = order.shopify.line_items[i];
      for (let i = 0; i < item.discount_allocations.length; i++) {
        const discount = item.discount_allocations[i];
        if (
          !this.isRedoItem(item) &&
          discountAllocations.find(
            (allocation: any) => allocation.code === discount.code,
          ) === undefined &&
          (distributionMethod === DiscountDistributionMethod.ORDER ||
            discount.discount_application_index == undefined) &&
          order.shopify.discount_applications[
            discount.discount_application_index
          ].target_selection === "entitled"
        ) {
          discountAllocations.push(item.discount_allocations[i]);
        }
      }
    }

    if (!discountAllocations?.length) {
      return 0;
    }

    let discountAmount = 0;
    discountAllocations.forEach((discount: any) => {
      // Ignore redo discounts as we want the return value to include
      // discounts from previous returns (i.e. customers should be
      // able to return items that were previously returned)

      // Discounts are distributed differently according to team settings
      let totalAmountDiscountedFromOrder = 0;
      let totalValueOfDiscountedItems = 0;

      if (distributionMethod === DiscountDistributionMethod.LINE_ITEM) {
        totalAmountDiscountedFromOrder = +discount.amount;
        totalValueOfDiscountedItems = +lineItem?.price * lineItem.quantity;
      } else {
        const acrossOrder =
          distributionMethod === DiscountDistributionMethod.ORDER;
        for (let i = 0; i < order.shopify.line_items.length; i++) {
          const item = order.shopify.line_items[i];
          if (!this.isRedoItem(item)) {
            totalValueOfDiscountedItems += acrossOrder
              ? +item.price * item.quantity
              : 0;
            for (let i = 0; i < item.discount_allocations.length; i++) {
              if (
                item.discount_allocations[i].discount_application_index ===
                discount.discount_application_index
              ) {
                totalAmountDiscountedFromOrder +=
                  +item.discount_allocations[i].amount;
                totalValueOfDiscountedItems += acrossOrder
                  ? 0
                  : +item.price * item.quantity;
              }
            }
          }
        }
      }

      const discount_code =
        order.shopify.discount_applications[discount.discount_application_index]
          ?.code;
      if (!discount_code?.includes("REDO") && totalValueOfDiscountedItems > 0) {
        discountAmount +=
          totalAmountDiscountedFromOrder *
          ((+lineItem?.price * lineItem.quantity) /
            totalValueOfDiscountedItems);
      }
    });

    return discountAmount;
  }

  static isRedoItem(lineItem: LineItem | undefined) {
    if (
      /^redo$|^re:do$/i.test(lineItem?.vendor) || // milwaukeemotorcycleclothing changed the vendor :/
      /route-/i.test(lineItem?.sku || "") ||
      /routeins/i.test(lineItem?.sku || "") ||
      /insurance|package\s+protection|order\s+protection/i.test(lineItem?.name)
    ) {
      return true;
    }
    return false;
  }

  static getTaxRateForLineItem(lineItem: LineItem) {
    return lineItem.tax_lines.reduce((total, taxLine) => {
      return total + taxLine.rate;
    }, 0);
  }

  // Goes through all products, adds up the price and the discount price and returns the percentage
  static getTotalDiscountPercentage(
    products: { line_item_id: string }[],
    order: Order,
  ) {
    let totalOrderPrice = 0;
    let totalOrderDiscountPrice = 0;
    const lineItems = products.map((product) => {
      return ReturnTotalsCalculator.findShopifyLineItem(
        product.line_item_id,
        order,
      );
    });

    lineItems.forEach((lineItem) => {
      totalOrderPrice += parseFloat(lineItem?.price);
      totalOrderDiscountPrice += parseFloat(
        ReturnTotalsCalculator.getDiscountPrice(lineItem, order),
      );
    });

    return (totalOrderPrice - totalOrderDiscountPrice) / totalOrderPrice || 0;
  }

  private static getOriginItem(
    lineItem: LineItem | undefined,
    order: Order | undefined,
  ) {
    let originItem: {
      unitPrice?: string;
      preDiscountPrice?: string;
      tax?: string;
    } | null = null;

    originItem =
      order?.originItems?.find((originItem) => {
        return (
          originItem.newVariantId === lineItem?.variant_id &&
          originItem.applicableShopifyOrder === order?.shopify.id
        );
      }) ||
      order?.originItems?.find(
        (originItem) => originItem.newVariantId === lineItem?.variant_id,
      ) ||
      null;
    if (!originItem) {
      const originItemJson = lineItem?.properties?.find(
        ({ name }: { name: string }) => name === "_redo_origin_item",
      );
      if (originItemJson) {
        originItem = JSON.parse(originItemJson.value);
      }
    }
    return originItem;
  }

  static getOriginItemPrice(
    lineItem: LineItem | undefined,
    order: Order | undefined,
  ) {
    const originItem = ReturnTotalsCalculator.getOriginItem(lineItem, order);

    return originItem?.preDiscountPrice || originItem?.unitPrice;
  }

  static getOriginItemDiscountPrice(
    lineItem: LineItem | undefined,
    order: Order | undefined,
  ) {
    const originItem = ReturnTotalsCalculator.getOriginItem(lineItem, order);

    return originItem?.unitPrice;
  }

  static getOriginItemTax(
    lineItem: LineItem | undefined,
    order: Order | undefined,
  ) {
    const originItem = ReturnTotalsCalculator.getOriginItem(lineItem, order);

    return originItem?.tax;
  }

  static getDiscountPrice(
    lineItem: LineItem | undefined,
    order: Order,
    distributionMethod?: DiscountDistributionMethod,
    product?: any,
  ) {
    if (lineItem && product?.isUnbundled) {
      const totalLineItemDiscount =
        ReturnTotalsCalculator.getDiscountAllocations(
          lineItem,
          order,
          distributionMethod,
        ) / lineItem.originalQuantity || lineItem.quantity;
      const percentOfBundle =
        parseFloat(product.price) / parseFloat(lineItem.price);

      return (
        parseFloat(product.price) -
        totalLineItemDiscount * percentOfBundle
      ).toFixed(2);
    }
    const originPrice = ReturnTotalsCalculator.getOriginItemDiscountPrice(
      lineItem,
      order,
    );
    if (originPrice) {
      return originPrice;
    }

    const discountAmount = ReturnTotalsCalculator.getDiscountAllocations(
      lineItem,
      order,
      distributionMethod,
    );

    return (
      (lineItem?.price || 0) -
      discountAmount / (lineItem?.quantity || 1)
    ).toFixed(2);
  }

  /**
   * Calculate totals for each product, including discount price, price adjustments, and tax
   */
  private calculateTotalsForProducts() {
    for (const product of this.return_.products) {
      this.productTotals.push(this.calculateTotalsForProduct(product));
    }
  }

  /**
   * Get the refund totals for a product
   */
  private getProductRefundTotals(product: Product) {
    let product_subtotal = 0;
    let tax_subtotal = 0;
    let refund_currency = "";
    for (const refund of this.return_.refunds || []) {
      for (const refundLineItem of refund.refund.refund_line_items) {
        if (refundLineItem.line_item_id === Number(product.line_item_id)) {
          product_subtotal += refundLineItem.subtotal;
          tax_subtotal += refundLineItem.total_tax;
          refund_currency =
            refundLineItem.subtotal_set.presentment_money.currency_code;
        }
      }
    }
    return {
      refund_item: product_subtotal,
      refund_tax: tax_subtotal,
      total_refund: product_subtotal + tax_subtotal,
      refund_currency,
    };
  }

  /**
   * Calculate totals for a specific product, including discount price, price adjustments, and tax
   */
  calculateTotalsForProduct(product: Product) {
    const shopifyLineItem = ReturnTotalsCalculator.findShopifyLineItem(
      product.line_item_id,
      this.order,
    );

    const tax = this.productTax(product);
    const discountPrice = product.isUnbundled
      ? parseFloat(product.price)
      : parseFloat(
          ReturnTotalsCalculator.getDiscountPrice(
            shopifyLineItem,
            this.order,
            this.team.settings.discountDistributionMethod,
          ),
        );

    const originPrice = ReturnTotalsCalculator.getOriginItemPrice(
      shopifyLineItem,
      this.order,
    );
    const originalPrice = originPrice
      ? parseFloat(originPrice)
      : product.isUnbundled
      ? parseFloat(product.price)
      : parseFloat(shopifyLineItem?.price);

    const { refund_item, refund_tax, total_refund, refund_currency } =
      this.getProductRefundTotals(product);

    return {
      product,
      discountPrice,
      originalPrice,
      priceAdjustment: parseFloat(
        this.getProductPriceAdjustment(product) || "0",
      ),
      merchantAdjustment: parseFloat(
        this.getProductMerchantAdjustment(product),
      ),
      finalPrice:
        discountPrice +
        tax +
        parseFloat(this.getProductPriceAdjustment(product) || "0") +
        parseFloat(this.getProductMerchantAdjustment(product)),
      tax,
      totalDiscount:
        ReturnTotalsCalculator.getDiscountAllocations(
          shopifyLineItem,
          this.order,
          this.team.settings.discountDistributionMethod,
        ) / (shopifyLineItem?.quantity || 1),
      refund_item,
      refund_tax,
      total_refund,
      refund_currency,
    };
  }

  /**
   * Calculate totals for all products of return
   */
  getTotalsForNonRejectedProducts(instantRefund: boolean = false) {
    return this.getTotalsForProducts(
      this.return_.products
        .filter((product) => product.status !== "rejected")
        .map((product) => ({
          _id: product._id,
          line_item_id: product.line_item_id,
        })),
      instantRefund,
    );
  }

  /**
   * Calculate totals for all products of return
   */
  getTotalsForAllProducts(instantRefund: boolean = false) {
    return this.getTotalsForProducts(
      this.return_.products.map((product) => ({
        _id: product._id,
        line_item_id: product.line_item_id,
      })),
      instantRefund,
    );
  }

  /**
   * Calculate totals for specific products
   */
  getTotalsForProducts(
    products: { line_item_id?: string; _id?: string }[],
    instantRefund: boolean = false,
  ) {
    // This accomodates calculating totals for products both before and after the return has been
    // created. If the return has been created, we really on the _id of the product. If not,
    // We rely on the line_item_id and quantities
    const productsToProcess: ProductTotals[] = [];
    this.productTotals.forEach((productTotals) => {
      // Ensure there aren't more productTotals with the same line item than are in lineItemIds
      const quantityRequested = products.filter(
        (product) =>
          product.line_item_id === productTotals.product.line_item_id,
      ).length;
      const quantityAdded = productsToProcess.filter(
        (product) =>
          product.product.line_item_id === productTotals.product.line_item_id,
      ).length;
      if (
        quantityAdded >= quantityRequested &&
        !products.some((product) => product._id !== null)
      ) {
        return;
      }
      if (
        products.some(
          (product) =>
            product._id === productTotals.product._id ||
            (!product._id &&
              product.line_item_id === productTotals.product.line_item_id),
        )
      ) {
        productsToProcess.push(productTotals);
      }
    });

    const storeCreditTotals = this.getStoreCreditAmount(productsToProcess);
    const refundTotals = this.getRefundAmount(productsToProcess);
    const newOrderValueTotals = this.getNewOrderValue(productsToProcess);
    const newOrderValue = newOrderValueTotals.amount + newOrderValueTotals.tax;

    const chargeTotals = {
      amount: newOrderValueTotals.amount,
      tax: newOrderValueTotals.tax,
    };

    // If there is a charge, apply any existing store credit
    if (chargeTotals.amount) {
      this.applyCreditToCharge(chargeTotals, storeCreditTotals);
    }

    // If there is still a charge, apply any existing refund
    if (chargeTotals.amount) {
      this.applyCreditToCharge(chargeTotals, refundTotals);
    }

    // For merchants like Wild Oak who use pre-discount prices
    // E.g. original price = $100, discount price = $60
    // The difference ($40) can be applied for shop now but cannot rollover as additional store credit
    const nonAccruableCredit = this.getNonAccruableCredit(productsToProcess);
    let nonAccruableLeftover = nonAccruableCredit.amount - chargeTotals.amount;
    if (nonAccruableLeftover < 0) {
      nonAccruableLeftover = 0;
    }
    chargeTotals.amount -= nonAccruableCredit.amount;
    chargeTotals.tax -= nonAccruableCredit.tax;
    if (chargeTotals.amount < 0) {
      chargeTotals.amount = 0;
    }
    if (chargeTotals.tax < 0) {
      chargeTotals.tax = 0;
    }

    const returnValueTotals = this.getTotalReturnValue(productsToProcess);
    const rawReturnValueTotals = this.getRawReturnValue(productsToProcess);
    const totalAdjustmentValue = this.getTotalAdjustmentValue();
    const totalTaxes = this.returnTax(this.return_);

    const totalReturnValue = returnValueTotals.amount + returnValueTotals.tax;
    let totalStoreCredit = storeCreditTotals.amount + storeCreditTotals.tax;
    let totalRefund = refundTotals.amount + refundTotals.tax;
    let displayCharge =
      newOrderValue -
      (totalReturnValue + nonAccruableCredit.amount + nonAccruableCredit.tax);
    if (displayCharge < 0) {
      displayCharge = 0;
    }

    let shippingFee =
      this.return_.shippingFeeToDeduct || this.return_.totals.fee;
    const salesChannelIdsToExclude = ["facebook"];
    const channelsToExclude = SalesChannels.filter((channel: Channel) =>
      salesChannelIdsToExclude.includes(channel.id),
    );
    let exclude = false;
    channelsToExclude.forEach((channel: Channel) => {
      channel.sourceNames.forEach((sourceName: string) => {
        if (this.order.shopify.source_name?.startsWith(sourceName)) {
          exclude = true;
        }
      });
    });
    // If we are not on the return portal (e.g. we're on the merchant dashboard), we should only deduct
    // the shipping fee if it was deducted in the return portal (i.e. labelDeductedFromCredit = true)
    if (
      !exclude &&
      (this.return_.labelDeductedFromCredit || this.isReturnPortal)
    ) {
      if (
        totalRefund > 0 &&
        shippingFee > 0 &&
        this.team.settings.deductLabelFromRefund
      ) {
        if (totalRefund >= shippingFee) {
          // Deduct the shipping cost from refund value, but only when it can cover the whole cost
          totalRefund = totalRefund - shippingFee;
          shippingFee = 0;
        }
      }

      if (
        this.team.settings.deductLabelFromCredit &&
        totalStoreCredit > 0 &&
        shippingFee > 0
      ) {
        if (totalStoreCredit > shippingFee) {
          // Deduct the shipping cost from store credit, but only when it can cover the whole cost
          totalStoreCredit = totalStoreCredit - shippingFee;
          shippingFee = 0;
        }
      }

      if (this.return_.shippingFeeToDeduct) {
        // The shipping cost needs to be reducted from the refund and/or store credit value,
        // but the return is being partially processed, so deduct some now and the rest later
        shippingFee -= totalRefund;
        totalRefund = shippingFee * -1;
        if (totalRefund < 0) {
          totalRefund = 0;
        }

        if (shippingFee > 0) {
          shippingFee -= totalStoreCredit;
          totalStoreCredit = shippingFee * -1;
          if (totalStoreCredit < 0) {
            totalStoreCredit = 0;
          }
        }

        if (shippingFee < 0) {
          shippingFee = 0;
        }
      }
    }

    let recoveryFee = 0;
    if (this.return_.provision === "instant") {
      if (this.return_.stripe) {
        recoveryFee = this.getRecoveryFee(
          productsToProcess,
          displayCharge,
          totalReturnValue,
          newOrderValue,
        );
      }
      // Rare case where there is an instant exchange and the new order value is more than the return value
      // If there are positive merchant adjustments, we need to add them to the store credit/refund
      // amounts instead of deducting them from the charge, since the draft order was already created
      // and it is no longer possible to change the price
      if (this.return_.draftOrderId) {
        chargeTotals.amount = 0;
        displayCharge = 0;

        if (
          newOrderValue >
          rawReturnValueTotals.amount + rawReturnValueTotals.tax
        ) {
          const storeCreditMerchantAdjustment = this.productTotals
            .filter(
              (product) =>
                this.getProductStrategy(product.product) === "store_credit",
            )
            .reduce((total, product) => {
              return total + product.merchantAdjustment;
            }, 0);

          const refundMerchantAdjustment = this.productTotals
            .filter(
              (product) =>
                this.getProductStrategy(product.product) === "refund",
            )
            .reduce((total, product) => {
              return total + product.merchantAdjustment;
            }, 0);

          totalStoreCredit = storeCreditMerchantAdjustment;
          if (totalStoreCredit < 0) {
            totalStoreCredit = 0;
          }

          totalRefund = refundMerchantAdjustment;
          if (totalRefund < 0) {
            totalRefund = 0;
          }
        }
      }
    }

    const newOrderDiscount = this.getReturnDiscountAllocations()
      ? this.getTotalNewOrderDiscountAllocations()
      : 0;
    const newOrderTaxes = parseFloat(this.getReturnNewOrderTaxes() || "0");

    const totalDiscount = this.getTotalDiscount(productsToProcess);

    let instantRefundFee = undefined;
    if (instantRefund) {
      instantRefundFee = totalRefund * INSTANT_REFUND_FEE_PERCENTAGE;
      totalRefund -= instantRefundFee;
      totalRefund = Math.max(totalRefund, 0);
    }

    const totals: ReturnTotals = {
      productTotals: this.productTotals,
      totalStoreCredit,
      totalRefund,
      totalReturnValue,
      returnValueTax: returnValueTotals.tax,
      totalRawReturnValue: rawReturnValueTotals.amount,
      totalAdjustmentValue,
      totalDiscount,
      newOrderValue,
      rawNewOrderValue: newOrderValueTotals.amount,
      newOrderDiscount,
      newOrderTaxes,
      totalTaxes,
      charge: chargeTotals.amount,
      displayCharge,
      recoveryFee: recoveryFee,
      shippingFee: shippingFee,
      instantRefundFee,
      shippingFeeDeducted: this.return_.totals.fee - shippingFee,
      nonAccruableCredit: nonAccruableCredit.amount,
      nonAccruableLeftover,
    };
    totals.productTotals = totals.productTotals.map((product) => {
      return {
        ...product,
        product: {
          _id: product.product._id || "",
          line_item_id: product.product.line_item_id,
          price: product.product.price,
          price_adjustment: product.product.price_adjustment,
          merchant_adjustment: product.product.merchant_adjustment || "",
          strategy: product.product.strategy,
          exchangeGroupItem: product.product.exchangeGroupItem || undefined,
        },
      };
    });

    return totals;
  }

  private applyCreditToCharge(chargeTotals: Totals, creditTotals: Totals) {
    const totalCharge = chargeTotals.amount + chargeTotals.tax;
    const totalCredit = creditTotals.amount + creditTotals.tax;

    if (totalCharge > totalCredit) {
      chargeTotals.amount -= creditTotals.amount;
      chargeTotals.tax -= creditTotals.tax;

      creditTotals.amount = 0;
      creditTotals.tax = 0;
    } else {
      creditTotals.amount -= chargeTotals.amount;
      creditTotals.tax -= chargeTotals.tax;

      chargeTotals.amount = 0;
      chargeTotals.tax = 0;
    }

    if (creditTotals.amount < 0 || creditTotals.tax < 0) {
      creditTotals.amount += creditTotals.tax;
      creditTotals.tax = 0;
    }

    if (chargeTotals.amount < 0 || chargeTotals.tax < 0) {
      chargeTotals.amount += chargeTotals.tax;
      chargeTotals.tax = 0;
    }
  }

  /**
   * Get the total price of all items being returned for a specific return type (credit, refund, or exchange)
   *
   * Note: the merchant adjustment (and price adjustment) is split between the product price and the product tax.
   *  Example: productPrice = $10, tax = $0.72, merchantAdjustment = -$5
   *  merchantAdjustmentPercentage = ($10 + $0.72 - $5) / ($10 + $0.72) = 0.534
   *  productPrice = $10 * 0.534 = $5.34
   *  tax = $0.72 * 0.534 = $0.38
   *  total = $5.34 + $0.38 = $5.72 (which is the same as $10 + $0.72 - $5)
   *
   * This is important when applying exchange discounts to draft orders, we apply it without taxes since
   *  taxes are calculated after discounts.
   */
  private getTotalValueForType(
    strategy: "refund" | "store_credit",
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    let amount = 0;
    let tax = 0;
    for (const productTotals of productsToProcess) {
      if (this.getProductStrategy(productTotals.product) === strategy) {
        if (includeAdjustments) {
          const adjustments =
            productTotals.priceAdjustment + productTotals.merchantAdjustment;
          const productPrice = productTotals.discountPrice + productTotals.tax;
          if (productPrice === 0) {
            amount += adjustments;
          } else {
            const adjustmentPercentage =
              (productPrice + adjustments) / productPrice;
            amount += productTotals.discountPrice * adjustmentPercentage;
            tax += productTotals.tax * adjustmentPercentage;
          }
        } else {
          amount += productTotals.discountPrice;
          tax += productTotals.tax;
        }
      }
    }

    return { amount, tax };
  }

  getTotalAdjustmentValue() {
    return this.productTotals.reduce((total, product) => {
      return total + product.priceAdjustment;
    }, 0);
  }

  getTotalDiscount(productTotals: ProductTotals[]) {
    return productTotals.reduce((total, product) => {
      return total + product.totalDiscount;
    }, 0);
  }

  /**
   * Total price of all items being returned for store credit
   */
  getStoreCreditAmount(
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    return this.getTotalValueForType(
      "store_credit",
      productsToProcess,
      includeAdjustments,
    );
  }

  /**
   * Total price of all items being returned for a refund
   */
  getRefundAmount(
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    return this.getTotalValueForType(
      "refund",
      productsToProcess,
      includeAdjustments,
    );
  }

  /**
   * Total price of all items being returned for a variant exchange
   */
  getVariantExchangeValue(
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    const variantExchangeProducts = productsToProcess.filter((productTotals) =>
      this.isVariantExchange(productTotals.product),
    );
    const storeCreditType = this.getTotalValueForType(
      "store_credit",
      variantExchangeProducts,
      includeAdjustments,
    );
    const refundType = this.getTotalValueForType(
      "refund",
      variantExchangeProducts,
      includeAdjustments,
    );

    return {
      amount: storeCreditType.amount + refundType.amount,
      tax: storeCreditType.tax + refundType.tax,
    };
  }

  /**
   * Total price of all new exchange items as part of the advanced exchange
   */
  getAdvancedExchangeItemsFee() {
    let exchangeFee = 0;
    for (const advancedExchangeItem of this.getReturnAdvancedExchangeItems()) {
      exchangeFee += parseFloat(advancedExchangeItem.price);
    }
    exchangeFee -= this.getTotalNewOrderDiscountAllocations();

    return {
      amount: exchangeFee,
      tax: parseFloat(this.getReturnNewOrderTaxes() || "0"),
    };
  }

  /**
   * Total price of all items being returned
   */
  getTotalReturnValue(productsToProcess: ProductTotals[]) {
    const storeCredit = this.getStoreCreditAmount(productsToProcess);
    const refund = this.getRefundAmount(productsToProcess);
    return {
      tax: storeCredit.tax + refund.tax,
      amount: storeCredit.amount + refund.amount,
    };
  }

  /**
   * Total price of all items being returned not including adjustments
   */
  getRawReturnValue(productsToProcess: ProductTotals[]) {
    const storeCredit = this.getStoreCreditAmount(productsToProcess, false);
    const refund = this.getRefundAmount(productsToProcess, false);
    return {
      tax: storeCredit.tax + refund.tax,
      amount: storeCredit.amount + refund.amount,
    };
  }

  /**
   * Total price of all items in the new order (including variant and advanced exchange items)
   */
  getNewOrderValue(productsToProcess: ProductTotals[]) {
    const variantExchangeValue = this.getVariantExchangeValue(
      productsToProcess,
      false,
    );
    const advancedExchangeItemsFee = this.getAdvancedExchangeItemsFee();
    return {
      tax: variantExchangeValue.tax + advancedExchangeItemsFee.tax,
      amount: variantExchangeValue.amount + advancedExchangeItemsFee.amount,
    };
  }

  getRecoveryFee(
    productsToProcess: ProductTotals[],
    charge: number,
    returnValue: number,
    newOrderValue: number,
  ) {
    const totalMerchantAdjustments = productsToProcess.reduce(
      (total, product) => total + product.merchantAdjustment,
      0,
    );
    if (returnValue - totalMerchantAdjustments > newOrderValue) {
      return charge;
    }

    return totalMerchantAdjustments > 0 ? 0 : totalMerchantAdjustments * -1;
  }

  getTotalNewOrderDiscountAllocations() {
    const discountAllocations = this.getReturnDiscountAllocations();
    if (!discountAllocations) {
      return 0;
    }

    return discountAllocations.reduce(
      (total: number, discount: DiscountAllocation) =>
        total + parseFloat(discount.discountedAmount.amount),
      0,
    );
  }

  isVariantExchange(product: Product) {
    return (
      product.exchangeGroupItem ||
      ("exchange_for" in product && product.exchange_for) ||
      ("new_item" in product && product.new_item)
    );
  }

  private static findShopifyLineItem(lineItemId: string, order: Order) {
    return order.shopify.line_items.find(
      (lineItem) => lineItem.id === Number(lineItemId),
    );
  }

  private getUsePreDiscountPrice() {
    return (
      (this.isReturnPortal &&
        this.team.settings.exchanges.usePreDiscountPrice) ||
      this.return_.usePreDiscountPrice
    );
  }

  private getNonAccruableCredit(productsToProcess: ProductTotals[]) {
    let tax = 0;
    let amount = 0;
    if (this.getUsePreDiscountPrice()) {
      for (const product of productsToProcess) {
        const nonAccruableAmount =
          product.originalPrice - product.discountPrice;

        amount += nonAccruableAmount;
        const shopifyLineItem = ReturnTotalsCalculator.findShopifyLineItem(
          product.product.line_item_id,
          this.order,
        );

        tax +=
          nonAccruableAmount *
          ReturnTotalsCalculator.getTaxRateForLineItem(shopifyLineItem!);
      }
    }

    return { amount, tax };
  }

  private getProductStrategy(product: Product) {
    if (this.useOriginalReturn && product.originalVariantOptions) {
      return product.originalVariantOptions.strategy;
    }
    return product.strategy;
  }

  private getProductPriceAdjustment(product: Product) {
    if (this.useOriginalReturn && product.originalVariantOptions) {
      return product.originalVariantOptions.price_adjustment;
    }
    return product.price_adjustment;
  }

  private getProductMerchantAdjustment(product: Product) {
    // Merchant adjustments happen after the return is created, so if we are
    // calculating for the original return, we shouldn't use merchant adjustments
    // (different than price_adjustment which is calculated in the return portal)
    if (this.useOriginalReturn || !product.merchant_adjustment) {
      return "0";
    }
    return product.merchant_adjustment;
  }

  private getReturnAdvancedExchangeItems() {
    if (this.useOriginalReturn && this.return_.originalReturnOptions) {
      return this.return_.originalReturnOptions.advancedExchangeItems || [];
    }
    return this.return_.advancedExchangeItems;
  }

  private getReturnNewOrderTaxes() {
    if (this.useOriginalReturn && this.return_.originalReturnOptions) {
      return this.return_.originalReturnOptions.new_order_taxes;
    }
    return this.return_.new_order_taxes;
  }

  private getReturnDiscountAllocations() {
    if (this.useOriginalReturn && this.return_.originalReturnOptions) {
      return this.return_.originalReturnOptions.discount_allocations;
    }
    return this.return_.discount_allocations;
  }
}
